import { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import HTMLFlipBook from "react-pageflip";
import { X, ChevronLeft, ChevronRight, Download, Maximize2, Loader2 } from "lucide-react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// Use a copy of pdfjs's worker that we ship from /public so we don't depend on
// a CDN (works offline + behind firewalls).
pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

const PDF_URL = "/assets/print-pricing-book-2026.pdf";

/**
 * Computes the page size that fits inside the viewport while keeping the
 * book's aspect ratio (8.5x11 letter ≈ 0.7727). Single-page on narrow viewports,
 * spread (2-page) on wider ones.
 */
const computeSize = () => {
  const W = window.innerWidth;
  const H = window.innerHeight;
  // Reserve room for top/bottom controls
  const maxH = H - 140;
  const aspect = 8.5 / 11;
  const isSpread = W >= 900;
  // Total content width budget; spread shows two pages side-by-side.
  const maxW = Math.min(W - 60, 1200);
  let pageW = isSpread ? maxW / 2 : maxW;
  let pageH = pageW / aspect;
  if (pageH > maxH) {
    pageH = maxH;
    pageW = pageH * aspect;
  }
  return { pageW: Math.floor(pageW), pageH: Math.floor(pageH), isSpread };
};

const PricingBookViewer = ({ onClose }) => {
  const flipRef = useRef(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0); // 0-indexed cursor
  const [size, setSize] = useState(computeSize());
  const [loaded, setLoaded] = useState(false);

  // Recompute size on resize / orientation change.
  useEffect(() => {
    const onResize = () => setSize(computeSize());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Keyboard nav (←/→/Esc) for desktop.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") flipRef.current?.pageFlip?.()?.flipNext();
      else if (e.key === "ArrowLeft") flipRef.current?.pageFlip?.()?.flipPrev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onDocLoad = ({ numPages }) => {
    setNumPages(numPages);
    setLoaded(true);
  };

  const onFlip = (e) => setCurrentPage(e.data);

  const goPrev = () => flipRef.current?.pageFlip?.()?.flipPrev();
  const goNext = () => flipRef.current?.pageFlip?.()?.flipNext();

  const requestFullscreen = () => {
    const root = document.getElementById("pricing-book-root");
    if (!root) return;
    if (document.fullscreenElement) document.exitFullscreen?.();
    else root.requestFullscreen?.();
  };

  // Display label: shows "Page X of Y" or "Pages X-Y of Z" in spread mode.
  const pageLabel = (() => {
    if (!numPages) return "Loading…";
    if (!size.isSpread) return `Page ${currentPage + 1} of ${numPages}`;
    const left = currentPage + 1;
    const right = Math.min(currentPage + 2, numPages);
    return left === right
      ? `Page ${left} of ${numPages}`
      : `Pages ${left}–${right} of ${numPages}`;
  })();

  return (
    <div
      id="pricing-book-root"
      className="fixed inset-0 z-[100] flex flex-col"
      style={{ background: "rgba(28, 25, 23, 0.96)" }}
      data-testid="pricing-book-viewer"
    >
      {/* Top bar */}
      <div
        className="flex items-center justify-between px-4 sm:px-6 py-3 border-b"
        style={{ background: "#1C1917", borderColor: "rgba(255,181,0,0.3)" }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="px-2 py-1 text-[10px] font-bold tracking-[0.18em] uppercase"
            style={{ background: "#FFB500", color: "#1C1917" }}
          >
            Catalog
          </div>
          <div className="text-white font-display text-sm sm:text-base font-bold truncate">
            Print Pricing Book — 2026
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <a
            href={PDF_URL}
            download="Print Pricing Book 2026.pdf"
            className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-white hover:bg-white/10 rounded-sm flex items-center gap-1.5"
            title="Download PDF"
            data-testid="pricing-book-download"
          >
            <Download size={13} /> <span className="hidden sm:inline">Download</span>
          </a>
          <button
            type="button"
            onClick={requestFullscreen}
            className="p-1.5 text-white hover:bg-white/10 rounded-sm"
            title="Toggle fullscreen"
            data-testid="pricing-book-fullscreen"
          >
            <Maximize2 size={14} />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-white hover:bg-white/10 rounded-sm"
            title="Close (Esc)"
            data-testid="pricing-book-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Book area */}
      <div className="flex-1 flex items-center justify-center px-3 sm:px-6 py-4 relative overflow-hidden">
        <Document
          file={PDF_URL}
          onLoadSuccess={onDocLoad}
          loading={
            <div className="text-white flex flex-col items-center gap-2">
              <Loader2 size={20} className="animate-spin" />
              <div className="text-xs font-mono opacity-75">Loading pricing book…</div>
            </div>
          }
          error={
            <div className="text-white text-sm">
              Could not load the pricing book.
              <a className="underline ml-1" href={PDF_URL} target="_blank" rel="noopener noreferrer">
                Open PDF in a new tab →
              </a>
            </div>
          }
        >
          {loaded && numPages > 0 && (
            <HTMLFlipBook
              ref={flipRef}
              width={size.pageW}
              height={size.pageH}
              size="fixed"
              minWidth={300}
              maxWidth={900}
              minHeight={400}
              maxHeight={1200}
              showCover
              flippingTime={650}
              usePortrait={!size.isSpread}
              maxShadowOpacity={0.5}
              drawShadow
              mobileScrollSupport
              swipeDistance={20}
              clickEventForward
              useMouseEvents
              onFlip={onFlip}
              className="pricing-flipbook"
              data-testid="pricing-book-flipbook"
            >
              {Array.from({ length: numPages }, (_, i) => (
                <div
                  key={i}
                  className="bg-white"
                  data-testid={`pricing-book-page-${i + 1}`}
                  style={{ boxShadow: "0 0 0 1px rgba(0,0,0,0.08)" }}
                >
                  <Page
                    pageNumber={i + 1}
                    width={size.pageW}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    loading={
                      <div
                        className="flex items-center justify-center text-stone-400 text-xs font-mono"
                        style={{ width: size.pageW, height: size.pageH }}
                      >
                        <Loader2 size={14} className="animate-spin mr-2" /> page {i + 1}
                      </div>
                    }
                  />
                </div>
              ))}
            </HTMLFlipBook>
          )}
        </Document>

        {/* Side arrows (hidden on touch since swipe is native) */}
        {loaded && (
          <>
            <button
              type="button"
              onClick={goPrev}
              className="hidden md:flex absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white"
              aria-label="Previous page"
              data-testid="pricing-book-prev"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={goNext}
              className="hidden md:flex absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white"
              aria-label="Next page"
              data-testid="pricing-book-next"
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </div>

      {/* Bottom bar */}
      <div
        className="flex items-center justify-center gap-4 sm:gap-6 px-4 py-3 border-t"
        style={{ background: "#1C1917", borderColor: "rgba(255,181,0,0.3)" }}
      >
        <button
          type="button"
          onClick={goPrev}
          className="md:hidden p-2 text-white hover:bg-white/10 rounded-sm"
          aria-label="Previous page"
        >
          <ChevronLeft size={18} />
        </button>
        <div
          className="text-white font-mono text-[11px] sm:text-xs tracking-wide"
          data-testid="pricing-book-page-indicator"
        >
          {pageLabel}
        </div>
        <button
          type="button"
          onClick={goNext}
          className="md:hidden p-2 text-white hover:bg-white/10 rounded-sm"
          aria-label="Next page"
        >
          <ChevronRight size={18} />
        </button>
        <div className="hidden sm:block text-white/60 text-[10px] font-mono">
          · swipe / drag · ← →
        </div>
      </div>
    </div>
  );
};

export default PricingBookViewer;
